import React, { useContext } from 'react';
import { useControlled } from '@mui/material';
import { useNonReactiveCallback } from '../../hooks/useNonReactiveCallback';
import { DatumId, ItemDescriptor } from './Chart.types';
import { useChartTooltipContext } from './tooltip/ChartTooltipProvider';

type ChartInteractionStateContextType = {
  highlightedItem: DatumId | null;
  isPinned: boolean;
  isInteractive: boolean;
  isPinnable: boolean;
};

type ChartInteractionDispatchContextType<Datum> = {
  setHighlight: (item: ItemDescriptor<Datum> | null) => void;
  handleItemClick: (event: React.MouseEvent, item: ItemDescriptor<Datum>) => void;
  showTooltip: (args: { data: Datum; x: number; y: number }) => void;
  hideTooltip: () => void;
};

const ChartInteractionStateContext = React.createContext<ChartInteractionStateContextType | undefined>(undefined);
const ChartInteractionDispatchContext = React.createContext<ChartInteractionDispatchContextType<any> | undefined>(undefined);

type ChartInteractionProviderProps<Datum> = {
  highlightedItem?: DatumId | null;
  onHighlightChange?: (item: ItemDescriptor<Datum> | null) => void;
  onItemClick?: (event: React.MouseEvent, item: ItemDescriptor<Datum>) => void;
  pinOnClick?: boolean;
};

export const ChartInteractionProvider = <Datum,>({
  children,
  highlightedItem: controlledHighlight,
  onHighlightChange,
  onItemClick,
  pinOnClick,
}: React.PropsWithChildren<ChartInteractionProviderProps<Datum>>) => {
  const [highlightedItem, setHighlightedItem] = useControlled<DatumId | null>({
    controlled: controlledHighlight,
    default: null,
    name: 'ChartInteractionProvider',
    state: 'highlightedItem',
  });

  const { isPinned, updatePosition, reset, showTooltip, hideTooltip, pin } = useChartTooltipContext<Datum>();

  const stableOnHighlightChange = useNonReactiveCallback(onHighlightChange);
  const stableOnItemClick = useNonReactiveCallback(onItemClick);

  const highlightedItemRef = React.useRef(highlightedItem);
  highlightedItemRef.current = highlightedItem;

  const setHighlight = React.useCallback(
    (item: ItemDescriptor<Datum> | null) => {
      if (isPinned) return;
      setHighlightedItem(item?.id ?? null);
      stableOnHighlightChange?.(item);
    },
    [isPinned, setHighlightedItem, stableOnHighlightChange]
  );

  const handleItemClick = React.useCallback(
    (event: React.MouseEvent, item: ItemDescriptor<Datum>) => {
      updatePosition(event.clientX, event.clientY);

      if (isPinned) {
        const isSame = highlightedItemRef.current === item.id;
        if (isSame) {
          reset();
          setHighlightedItem(null);
          stableOnHighlightChange?.(null);
          return;
        }
        showTooltip({ data: item.datum, x: event.clientX, y: event.clientY });
        setHighlightedItem(item.id);
        stableOnHighlightChange?.(item);
      } else {
        showTooltip({ data: item.datum, x: event.clientX, y: event.clientY });
        if (pinOnClick) pin();
        setHighlightedItem(item.id);
        stableOnHighlightChange?.(item);
      }

      stableOnItemClick?.(event, item);
    },
    [isPinned, pin, pinOnClick, reset, setHighlightedItem, showTooltip, stableOnHighlightChange, stableOnItemClick, updatePosition]
  );

  const isInteractive = !!onItemClick || !!pinOnClick;
  const isPinnable = !!pinOnClick;

  const stateValue = React.useMemo(
    () => ({ highlightedItem, isPinned, isInteractive, isPinnable }),
    [highlightedItem, isPinned, isInteractive, isPinnable]
  );

  const dispatchValue = React.useMemo(
    () => ({ setHighlight, handleItemClick, showTooltip, hideTooltip }),
    [setHighlight, handleItemClick, showTooltip, hideTooltip]
  );

  return (
    <ChartInteractionStateContext.Provider value={stateValue}>
      <ChartInteractionDispatchContext.Provider value={dispatchValue}>{children}</ChartInteractionDispatchContext.Provider>
    </ChartInteractionStateContext.Provider>
  );
};

export const useChartInteractionState = () => {
  const ctx = useContext(ChartInteractionStateContext);
  if (!ctx) throw new Error('useChartInteractionState must be used within a ChartInteractionProvider');
  return ctx;
};

export const useChartInteractionDispatch = <Datum,>() => {
  const ctx = useContext(ChartInteractionDispatchContext) as ChartInteractionDispatchContextType<Datum> | undefined;
  if (!ctx) throw new Error('useChartInteractionDispatch must be used within a ChartInteractionProvider');
  return ctx;
};
